import KollectiveXHR from '../interceptor/KollectiveXHR';
import OldXhrLoader from './HlsJsLoaderFactory';
import VersionOneXhrLoader, {
  XhrSetupFunction,
  XhrFactoryFunction
} from './HlsJsXhrLoaderFactory';
import logger from '../log';
import { Logger } from '../log/Logger';
import { BindOptions } from '../player/interfaces';
import {
  ErrorMessages,
  HLSjsConfig,
  HLSjsConfigWrapper,
  HLSConstructor
} from '@kollective-frontend/ksdk-api';
import { HLS } from '../player/interfaces';
import { isString } from '../common/utils/stringify';
import { dispatchError } from '../events/dispatch';
import { InterceptBinder } from '../player/utils/InterceptBinder';

const log = logger.getLogger(__filename);
// ADD global: window.Hls
declare global {
  interface Window {
    Hls: HLSConstructor;
  }
}

// ADD global: window.Hls
declare global {
  interface Window {
    Hls: HLSConstructor;
  }
}

export function loaderFactory(
  binder: InterceptBinder,
  isV1: boolean
): XhrLoaderGenerator {
  const XhrLoader = isV1 ? VersionOneXhrLoader : OldXhrLoader;
  const xhrFactory = (): XMLHttpRequest => {
    if (binder.shouldIntercept()) {
      log.debug('using KollectiveXHR for request');
      const opts = binder.getOptions() || {};
      // KollectiveXHR is a dropin for XMLHttpRequest, but it is missing a few
      // functions, and implements a couple other functions differently.
      return (new KollectiveXHR(opts) as unknown) as XMLHttpRequest;
    }
    return new window.XMLHttpRequest();
  };
  return (xhrConfig: XhrLoaderConfig): XhrLoaderObject => {
    return new XhrLoader({ ...xhrConfig, xhrFactory });
  };
}

export const handleError = (
  reason: string,
  log: Logger,
  ...other: unknown[]
): void => {
  dispatchError(reason);
  log.error(reason, ...other);
};

export const bindToWebRTC = (
  hls: HLSjsConfigWrapper | HLS,
  isV1: boolean,
  binder: InterceptBinder
): HLSjsConfig => {
  const loader = loaderFactory(binder, isV1);
  const { config } = hls;
  // check for fLoader or pLoader as they override loader
  // https://github.com/video-dev/hls.js/blob/master/docs/API.md#loader
  if (config && (config.pLoader || config.fLoader)) {
    handleError(ErrorMessages.HLS_CONFLICTING_LOADERS, log, hls);
  }
  // the code expects an hls object created from new Hls({}). In this case the loader will be on the inner config object.
  if (config) {
    config.loader = loader;
  } else {
    handleError(ErrorMessages.HLS_MISSING_CONFIG, log, { hls });
  }
  return { ...config, loader };
};

/**
 * Bind intercept to HLSjsConfig
 */
export const bindHLSjsConfigToWebRTC = (
  data: HLSjsConfigWrapper,
  binder: InterceptBinder
): HLSjsConfig => {
  const majorVersion = parseVersion(data.options.version).major;
  if (isNaN(majorVersion)) {
    handleError(ErrorMessages.HLS_MISSING_VERSION, log);
  }
  const isV1 = !isNaN(majorVersion) && majorVersion >= 1;
  return bindToWebRTC(data, isV1, binder);
};

/**
 * Bind intercept to HLSjs.
 */
export default function bindHLSjsToWebRTC(
  hls: HLS,
  binder: InterceptBinder
): HLSjsConfig {
  return bindToWebRTC(hls, isVersionOne(hls as HLSv1), binder);
}

// Interfaces
export type BindOptionsFunction = () => BindOptions;
export type BoolFunction = () => boolean;
export type XhrLoaderGenerator = (
  xhrConfig: XhrLoaderConfig
) => XhrLoaderObject;
export type XhrLoaderObject = VersionOneXhrLoader | OldXhrLoader;

export interface XhrLoaderConfig {
  xhrSetup: XhrSetupFunction;
  xhrFactory: XhrFactoryFunction;
}

// Utils
export interface VersionObject {
  major: number;
  minor: number;
  patch: number;
}

const parseVersion = (version?: string): VersionObject => {
  const obj = { major: NaN, minor: NaN, patch: NaN };
  if (isString(version)) {
    const items = version.split(/\./);
    obj.major = parseInt(items[0]);
    obj.minor = items.length > 1 ? parseInt(items[1]) : NaN;
    obj.patch = items.length > 2 ? parseInt(items[2]) : NaN;
  }
  return obj;
};

interface HLSv1 extends HLS {
  _media?: unknown;
  bufferController?: unknown;
  latencyController?: unknown;
}

export const isVersionOne = (hls?: HLSv1): boolean => {
  // Use the Hls object directly if available:
  if (window.Hls) {
    const version = parseVersion(window.Hls.version);
    if (!isNaN(version.major)) {
      return version.major >= 1;
    }
  }
  // Version one of hls has new components the bufferController,
  // latencyController, and moved media to _media, as well as other differences.
  return (
    typeof hls !== 'undefined' &&
    (typeof hls._media !== 'undefined' ||
      typeof hls.bufferController !== 'undefined' ||
      typeof hls.latencyController !== 'undefined')
  );
};
